import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { BarcodeScanner } from '@/components/BarcodeScanner';
import { VoiceLogger } from '@/components/VoiceLogger';
import { MealPrepTab } from '@/components/MealPrepTab';
import { MenuScanner } from '@/components/MenuScanner';
import { NearbyRestaurantCard, RestaurantMenuPanel } from '@/components/NearbyRestaurantCard';
import { useNearbyRestaurant } from '@/hooks/useNearbyRestaurant';
import { ArrowLeft, Loader2, Search } from 'lucide-react';
import ScreenBackground from '@/components/ScreenBackground';
import SmartLogConfirm from '@/components/SmartLogConfirm';
import axios from 'axios';
import { toast } from 'sonner';
import logger from '@/utils/logger';

import { MealTypeSelector, CapturePhotoView, SubViewWrapper } from '@/components/log-meal/CapturePhotoView';
import { ResultPanel } from '@/components/log-meal/ResultPanel';
import { FollowUpView } from '@/components/log-meal/FollowUpView';
import { ConfirmedView } from '@/components/log-meal/ConfirmedView';
import { DebugPreviewModal } from '@/components/log-meal/DebugPreviewModal';

const API = '/api';

export default function LogMeal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authHeaders, token } = useAuth();
  const fileInputRef = useRef(null);
  const defaultTab = location.state?.defaultTab === 'eat-out' ? 'restaurant' : 'photo';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [phase, setPhase] = useState('capture');
  const [analysis, setAnalysis] = useState(null);
  const [mealData, setMealData] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [mealType, setMealType] = useState('lunch');
  const [error, setError] = useState('');
  const [confirmError, setConfirmError] = useState(false);
  const [editableItems, setEditableItems] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [expandedRestaurant, setExpandedRestaurant] = useState(null);
  const [manualZip, setManualZip] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [geocodingManual, setGeocodingManual] = useState(false);
  const [showManualLocation, setShowManualLocation] = useState(false);
  const [searchRadiusMi, setSearchRadiusMi] = useState(3);
  const [textInput, setTextInput] = useState('');
  const [textAnalyzing, setTextAnalyzing] = useState(false);
  const textInputRef = useRef(null);
  const [coachingReview, setCoachingReview] = useState(null);
  const [showCoachingCard, setShowCoachingCard] = useState(false);
  const [voiceEditListening, setVoiceEditListening] = useState(false);
  const [voiceEditProcessing, setVoiceEditProcessing] = useState(false);
  const voiceEditRecorder = useRef(null);
  const voiceEditChunks = useRef([]);
  const [debugPreview, setDebugPreview] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [foodMatch, setFoodMatch] = useState(null);
  const [foodMatchDismissed, setFoodMatchDismissed] = useState(false);

  useEffect(() => {
    const h = new Date().getHours();
    if (h < 10) setMealType('breakfast');
    else if (h < 15) setMealType('lunch');
    else if (h < 19) setMealType('dinner');
    else setMealType('snack');
  }, []);

  const {
    nearbyData, loading: nearbyLoading, dismiss: dismissNearby,
    permissionState, requestPermission, setManualCoords
  } = useNearbyRestaurant(phase === 'capture');

  const handleManualGeocode = async () => {
    if (!manualZip && !manualCity) return;
    setGeocodingManual(true);
    try {
      const res = await axios.post(`${API}/geolocation/geocode`, { zipcode: manualZip, city: manualCity }, authHeaders());
      if (res.data.lat && res.data.lng) {
        setManualCoords(res.data.lat, res.data.lng, searchRadiusMi);
        setShowManualLocation(false);
      }
    } catch { toast.error('Something went wrong logging your meal'); }
    setGeocodingManual(false);
  };

  const handleTextSubmit = async () => {
    const text = textInput.trim();
    if (!text) return;

    // Check food library for a match before hitting AI
    if (!foodMatchDismissed) {
      try {
        const matchRes = await axios.get(`${API}/food-library/match?name=${encodeURIComponent(text)}`, authHeaders());
        if (matchRes.data.match) {
          setFoodMatch(matchRes.data.match);
          return; // Show match prompt instead of analyzing
        }
      } catch { toast.error('Something went wrong logging your meal'); }
    }

    setFoodMatch(null);
    setFoodMatchDismissed(false);
    setTextAnalyzing(true);
    setError('');
    try {
      const res = await axios.post(`${API}/meals/voice`, { transcription: text, meal_type: mealType }, { headers: { Authorization: `Bearer ${token}` } });
      setMealData(res.data);
      setAnalysis(res.data.initial_analysis || res.data.final_analysis);
      setEditableItems(res.data.initial_analysis?.items || res.data.final_analysis?.items || []);
      setPhase('result');
      fetchSuggestion(res.data.meal_id);
    } catch (err) {
      logger.error('Text analysis failed:', err);
      if (err.response?.status === 429) {
        setError(err.response?.data?.detail || 'AI budget exhausted. Upgrade your plan for more AI access.');
      } else {
        setError('Failed to analyze meal. Please try again.');
      }
    }
    setTextAnalyzing(false);
  };

  const handleFoodMatchRelog = async () => {
    if (!foodMatch) return;
    setTextAnalyzing(true);
    try {
      await axios.post(`${API}/food-library/log-from-library?food_id=${foodMatch.id}&servings=1`, {}, authHeaders());
      await axios.post(`${API}/meals/log-text`, {
        text: `${foodMatch.name} (from My Foods)`,
        confirmed: true,
      }, authHeaders());
      setFoodMatch(null);
      setTextInput('');
      setPhase('capture');
      navigate('/dashboard');
    } catch {
      setError('Could not log from library');
    }
    setTextAnalyzing(false);
  };

  const dismissFoodMatch = () => {
    setFoodMatch(null);
    setFoodMatchDismissed(true);
    // Re-submit to AI
    setTimeout(() => handleTextSubmit(), 100);
  };

  const pollForReview = async (mealId) => {
    for (let i = 0; i < 8; i++) {
      await new Promise(r => setTimeout(r, 1500));
      try {
        const res = await axios.get(`${API}/meals/${mealId}/review`, authHeaders());
        if (res.data?.review) { setCoachingReview(res.data.review); setShowCoachingCard(true); return; }
      } catch { toast.error('Something went wrong logging your meal'); }
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const MAX = 1024;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else { w = Math.round(w * MAX / h); h = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Compression failed'));
            const reader = new FileReader();
            reader.onload = (e) => resolve({ blob, dataUrl: e.target.result });
            reader.readAsDataURL(blob);
          }, 'image/jpeg', 0.85
        );
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    compressImage(file).then(({ blob, dataUrl }) => {
      setImageFile(blob); setImagePreview(dataUrl); autoAnalyze(blob);
    }).catch(() => {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
      autoAnalyze(file);
    });
  };

  const showDebugCompress = async () => {
    if (!imageFile) return;
    setDebugLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      const res = await axios.post(`${API}/meals/debug-compress`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      setDebugPreview(res.data);
    } catch (err) { logger.error('Debug compress failed:', err); }
    setDebugLoading(false);
  };

  const autoAnalyze = async (file) => {
    setPhase('analyzing'); setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/meals/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      setMealData(res.data);
      setAnalysis(res.data.initial_analysis || res.data.final_analysis);
      setEditableItems(res.data.initial_analysis?.items || res.data.final_analysis?.items || []);
      setPhase('result');
      fetchSuggestion(res.data.meal_id);
    } catch (err) {
      logger.error('Analysis failed:', err);
      if (err.response?.status === 429) {
        setError(err.response?.data?.detail || 'AI budget exhausted. Upgrade your plan for more AI access.');
      } else { setError('Failed to analyze meal. Please try again.'); }
      setPhase('capture');
    }
  };

  const handleAnalyze = async () => { if (imageFile) autoAnalyze(imageFile); };

  const saveEdits = async () => {
    if (!mealData) return;
    try {
      const res = await axios.put(`${API}/meals/${mealData.meal_id}/edit`, { items: editableItems, meal_type: mealType }, authHeaders());
      setMealData(res.data); setAnalysis(res.data.final_analysis);
    } catch (err) { logger.error('Save edits failed:', err); }
  };

  const handleStartFollowUp = () => {
    if (!mealData?.follow_up_questions?.length) { handleConfirm(); return; }
    saveEdits(); setCurrentQ(0); setAnswers([]); setPhase('followup');
  };

  const fetchSuggestion = async (mealId) => {
    setLoadingSuggestion(true);
    try { const res = await axios.post(`${API}/meals/${mealId}/suggestion`, {}, authHeaders()); setSuggestion(res.data); }
    catch (err) { logger.error('Suggestion failed:', err); }
    setLoadingSuggestion(false);
  };

  const handleAnswer = async (questionId, option) => {
    const newAnswers = [...answers, { question_id: questionId, answer: option.label, calorie_adjustment: option.calorie_adjustment }];
    setAnswers(newAnswers);
    const questions = mealData.follow_up_questions;
    if (currentQ < questions.length - 1) { setCurrentQ(currentQ + 1); }
    else {
      try {
        const res = await axios.post(`${API}/meals/followup`, { meal_id: mealData.meal_id, answers: newAnswers }, authHeaders());
        setMealData(res.data); setAnalysis(res.data.final_analysis); setPhase('result');
      } catch (err) { logger.error('Followup failed:', err); setPhase('result'); }
    }
  };

  const handleConfirm = async () => {
    setConfirmError(false);
    try {
      if (editableItems.length > 0 && mealData) {
        await axios.put(`${API}/meals/${mealData.meal_id}/edit`, { items: editableItems, meal_type: mealType }, authHeaders());
      }
      const res = await axios.post(`${API}/meals/${mealData.meal_id}/confirm`, { meal_type: mealType }, authHeaders());
      const isFirstMeal = res.data?.total_meals_logged === 1;
      setPhase('confirmed');
      window.dispatchEvent(new Event('nuri-meal-logged'));
      pollForReview(mealData.meal_id);
      setTimeout(() => {
        if (!showCoachingCard) navigate('/dashboard', isFirstMeal ? { state: { celebration: 'first_meal' } } : undefined);
      }, 2500);
    } catch (err) { logger.error('Confirm failed:', err); setConfirmError(true); }
  };

  const handleBarcodeLogged = (meal) => {
    const isFirstMeal = meal?.total_meals_logged === 1;
    setPhase('confirmed');
    window.dispatchEvent(new Event('nuri-meal-logged'));
    setTimeout(() => navigate('/dashboard', isFirstMeal ? { state: { celebration: 'first_meal' } } : undefined), 1500);
  };

  const startVoiceEdit = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      voiceEditChunks.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) voiceEditChunks.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(voiceEditChunks.current, { type: 'audio/webm' });
        await processVoiceEdit(blob);
      };
      recorder.start(); voiceEditRecorder.current = recorder; setVoiceEditListening(true);
    } catch {
      const correction = window.prompt('Describe the correction:');
      if (correction) processVoiceEditText(correction);
    }
  };

  const stopVoiceEdit = () => {
    if (voiceEditRecorder.current?.state === 'recording') { voiceEditRecorder.current.stop(); setVoiceEditListening(false); }
  };

  const processVoiceEdit = async (blob) => {
    setVoiceEditProcessing(true);
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'voice_edit.webm');
      const transcribeRes = await axios.post(`${API}/voice-convo/transcribe`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (transcribeRes.data.transcript) await processVoiceEditText(transcribeRes.data.transcript);
    } catch (err) { logger.error('Voice edit transcription failed:', err); }
    setVoiceEditProcessing(false);
  };

  const processVoiceEditText = async (correction) => {
    setVoiceEditProcessing(true);
    try {
      const currentItems = editableItems.map(i => `${i.name} (${i.calories} cal, ${i.protein}g P, ${i.carbs}g C, ${i.fat}g F)`).join(', ');
      const prompt = `The user is correcting a meal they just logged. Current items: ${currentItems}. Their correction: "${correction}". Apply all corrections. Return ONLY the corrected full meal as a JSON action block.`;
      const res = await axios.post(`${API}/voice-convo/chat`, { transcript: prompt }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.logged_meal_id) {
        const mealRes = await axios.get(`${API}/meals/${res.data.logged_meal_id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (mealRes.data?.final_analysis?.items) {
          setEditableItems(mealRes.data.final_analysis.items);
          setAnalysis(mealRes.data.final_analysis);
          try { await axios.post(`${API}/voice-convo/undo`, { meal_id: res.data.logged_meal_id }, { headers: { Authorization: `Bearer ${token}` } }); } catch { toast.error('Something went wrong logging your meal'); }
        }
      }
    } catch (err) { logger.error('Voice edit processing failed:', err); }
    setVoiceEditProcessing(false);
  };

  const handleVoiceAnalysis = (meal) => {
    setMealData(meal);
    setAnalysis(meal.initial_analysis || meal.final_analysis);
    setEditableItems(meal.initial_analysis?.items || meal.final_analysis?.items || []);
    setPhase('result');
    fetchSuggestion(meal.meal_id);
  };

  const totals = editableItems.length > 0
    ? editableItems.reduce((acc, i) => ({ calories: acc.calories + (i.calories || 0), protein: acc.protein + (i.protein || 0), carbs: acc.carbs + (i.carbs || 0), fat: acc.fat + (i.fat || 0) }), { calories: 0, protein: 0, carbs: 0, fat: 0 })
    : (analysis?.totals || { calories: 0, protein: 0, carbs: 0, fat: 0 });
  const conf = mealData?.confidence || analysis?.confidence || 0;
  const questions = mealData?.follow_up_questions || [];
  const accuracyDelta = mealData?.accuracy_delta || 0;

  const prefillMeal = location.state?.prefillMeal;
  if (prefillMeal) return <SmartLogConfirm meal={prefillMeal} onClose={() => navigate(-1)} />;

  return (
    <ScreenBackground screen="meal_logging" special="capture" data-testid="log-meal-page">
      <header style={{ borderBottom: '1px solid #E8E3DB', background: '#FDFBF7', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', height: 56, gap: 12 }}>
          <button data-testid="back-btn" onClick={() => navigate('/dashboard')} style={{ padding: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#1A1A18' }}>
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 400, color: '#1A1A18', margin: 0 }}>Log Meal</h1>
        </div>
      </header>

      <main style={{ maxWidth: 600, margin: '0 auto', padding: '16px 24px 120px' }}>
        {/* Food Library Match Prompt */}
        {foodMatch && (
          <div data-testid="food-match-prompt" style={{
            padding: '16px 18px', borderRadius: 16, marginBottom: 16,
            background: '#FFFFFF', border: '1.5px solid #5A8C6A',
            boxShadow: '0 2px 12px rgba(90,140,106,0.12)',
          }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#5A8C6A', margin: '0 0 4px', fontFamily: 'var(--font-sans)' }}>
              Is this the same {foodMatch.name}?
            </p>
            <p style={{ fontSize: 12, color: '#6B6560', margin: '0 0 12px', fontFamily: 'var(--font-sans)', lineHeight: 1.4 }}>
              {foodMatch.calories} cal · P {foodMatch.protein}g · C {foodMatch.carbs}g · F {foodMatch.fat}g
              {foodMatch.times_logged > 1 ? ` · Logged ${foodMatch.times_logged}x before` : ''}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button data-testid="food-match-yes" onClick={handleFoodMatchRelog}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 12, border: 'none',
                  background: '#5A8C6A', color: '#fff', fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'var(--font-sans)',
                }}>
                Yes — log it!
              </button>
              <button data-testid="food-match-no" onClick={dismissFoodMatch}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 12,
                  border: '1px solid #E8E3DB', background: 'transparent',
                  color: '#6B6560', fontSize: 14, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'var(--font-sans)',
                }}>
                No, it's different
              </button>
            </div>
          </div>
        )}

        {phase === 'capture' && activeTab === 'photo' && (
          <CapturePhotoView mealType={mealType} setMealType={setMealType} imagePreview={imagePreview} imageFile={imageFile}
            fileInputRef={fileInputRef} textInput={textInput} setTextInput={setTextInput} textAnalyzing={textAnalyzing}
            textInputRef={textInputRef} handleFileSelect={handleFileSelect} handleTextSubmit={handleTextSubmit}
            handleAnalyze={handleAnalyze} error={error} setImagePreview={setImagePreview} setImageFile={setImageFile}
            setError={setError} setActiveTab={setActiveTab} navigate={navigate} />
        )}

        {phase === 'capture' && activeTab === 'barcode' && (
          <SubViewWrapper onBack={() => setActiveTab('photo')}>
            <MealTypeSelector mealType={mealType} setMealType={setMealType} />
            <BarcodeScanner onMealLogged={handleBarcodeLogged} mealType={mealType} />
          </SubViewWrapper>
        )}

        {phase === 'capture' && activeTab === 'voice' && (
          <SubViewWrapper onBack={() => setActiveTab('photo')}>
            <MealTypeSelector mealType={mealType} setMealType={setMealType} />
            <VoiceLogger onAnalysisComplete={handleVoiceAnalysis} mealType={mealType} />
          </SubViewWrapper>
        )}

        {phase === 'capture' && activeTab === 'prep' && (
          <SubViewWrapper onBack={() => setActiveTab('photo')}>
            <MealPrepTab onBatchCreated={() => { setPhase('confirmed'); setTimeout(() => navigate('/dashboard'), 1500); }} />
          </SubViewWrapper>
        )}

        {phase === 'capture' && activeTab === 'menu' && (
          <SubViewWrapper onBack={() => setActiveTab('photo')}>
            <MealTypeSelector mealType={mealType} setMealType={setMealType} />
            <MenuScanner mealType={mealType}
              onItemSelect={(items) => {
                setEditableItems(items);
                setAnalysis({ items, totals: items.reduce((acc, i) => ({ calories: acc.calories + i.calories, protein: acc.protein + i.protein, carbs: acc.carbs + i.carbs, fat: acc.fat + i.fat }), { calories: 0, protein: 0, carbs: 0, fat: 0 }) });
                const desc = items.map(i => i.name).join(', ');
                axios.post(`${API}/meals/voice`, { transcription: desc, meal_type: mealType }, { headers: { Authorization: `Bearer ${token}` } })
                  .then(res => { setMealData(res.data); setEditableItems(items); setPhase('result'); })
                  .catch(() => { setMealData({ meal_id: `temp_${Date.now()}`, follow_up_questions: [], confidence: 70 }); setPhase('result'); });
              }}
            />
          </SubViewWrapper>
        )}

        {phase === 'analyzing' && (
          <div className="animate-fade-in-up">
            <div className="relative">
              <img src={imagePreview} alt="Meal" className="w-full aspect-[4/3] object-cover rounded-2xl" />
              <div className="absolute inset-0 rounded-2xl glass-panel flex flex-col items-center justify-center">
                <div className="shimmer-bg w-full h-full absolute rounded-2xl" />
                <div className="relative z-10 text-center">
                  <Loader2 className="w-10 h-10 text-[#1A3A4A] dark:text-[#6B9E8A] animate-spin mx-auto mb-3" />
                  <p className="font-semibold text-[#1A3A4A] dark:text-[#F0EDE8] text-lg">Analyzing your meal...</p>
                  <p className="text-sm text-[#7A7A72] dark:text-[#A1A1AA] mt-1">AI is identifying food items</p>
                </div>
              </div>
            </div>
            <button data-testid="debug-preview-btn" onClick={showDebugCompress} disabled={debugLoading}
              style={{ margin: '12px auto 0', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 16, border: '1px dashed #C67A5C', background: 'rgba(198,122,92,0.05)', cursor: 'pointer', fontSize: 11, color: '#C67A5C', fontFamily: 'var(--font-sans)', fontWeight: 500 }}>
              {debugLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
              View image sent to AI
            </button>
          </div>
        )}

        {phase === 'result' && analysis && (
          <ResultPanel imagePreview={imagePreview} imageFile={imageFile} analysis={analysis} totals={totals}
            confidence={conf} accuracyDelta={accuracyDelta} editableItems={editableItems} setEditableItems={setEditableItems}
            suggestion={suggestion} setSuggestion={setSuggestion} questions={questions} mealData={mealData}
            voiceEditListening={voiceEditListening} voiceEditProcessing={voiceEditProcessing}
            startVoiceEdit={startVoiceEdit} stopVoiceEdit={stopVoiceEdit}
            handleStartFollowUp={handleStartFollowUp} handleConfirm={handleConfirm}
            confirmError={confirmError} showDebugCompress={showDebugCompress} debugLoading={debugLoading} navigate={navigate} />
        )}

        {phase === 'followup' && questions[currentQ] && (
          <FollowUpView imagePreview={imagePreview} totals={totals} questions={questions}
            currentQ={currentQ} handleAnswer={handleAnswer} />
        )}

        {phase === 'confirmed' && (
          <ConfirmedView showCoachingCard={showCoachingCard} coachingReview={coachingReview} navigate={navigate} />
        )}
      </main>

      <DebugPreviewModal debugPreview={debugPreview} onClose={() => setDebugPreview(null)} />
    </ScreenBackground>
  );
}
